import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import { StyledTableCell, formatMoney } from "../styles";
import InvoiceSummary from "./InvoiceSummary";

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "CHECK", label: "Check" },
  { value: "WIRE TRANSFER", label: "Wire Transfer" },
  { value: "DIRECT DEPOSIT", label: "Direct Deposit" },
  { value: "BANK TRANSFER", label: "Bank Transfer" },
  { value: "CREDIT CARD", label: "Credit Card" },
  { value: "GCASH", label: "GCash" },
  { value: "PAYPAL", label: "Paypal" },
  { value: "OFFSET", label: "Offset" },
  { value: "POST DATED CHECK", label: "Post dated check" },
];

/**
 * Payment details -- summary, add-payment form, and payment history.
 *
 * `form` holds the five add-payment fields and `onFormChange(field, value)`
 * updates one of them. That is one prop pair instead of ten, and adding a
 * sixth field later does not change this component's signature.
 */
const PaymentDialog = ({
  open,
  loading,
  saving,
  invoice,
  payments,
  form,
  onFormChange,
  onSave,
  onClose,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>Payment Details</DialogTitle>

    <DialogContent dividers>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: 250 }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <InvoiceSummary invoice={invoice} />

          <Divider />

          <Box mt={3}>
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, marginBottom: 15 }}
            >
              Add Payment
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Payment Amount"
                  type="number"
                  value={form.amount}
                  onChange={(e) => onFormChange("amount", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel>Payment Method</InputLabel>

                  <Select
                    value={form.method}
                    onChange={(e) => onFormChange("method", e.target.value)}
                    label="Payment Method"
                  >
                    <MenuItem value="">Select</MenuItem>

                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Payment Date"
                  type="datetime-local"
                  value={form.date}
                  onChange={(e) => onFormChange("date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Reference No."
                  value={form.reference}
                  onChange={(e) => onFormChange("reference", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Remarks"
                  value={form.remarks}
                  onChange={(e) => onFormChange("remarks", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={4}>
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, marginBottom: 12 }}
            >
              Payment History
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Date</StyledTableCell>
                    <StyledTableCell>Amount</StyledTableCell>
                    <StyledTableCell>Method</StyledTableCell>
                    <StyledTableCell>Reference</StyledTableCell>
                    <StyledTableCell>Remarks</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {payments.length > 0 ? (
                    payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <StyledTableCell>
                          {payment.payment_date
                            ? new Date(payment.payment_date).toLocaleString()
                            : "---"}
                        </StyledTableCell>

                        <StyledTableCell>
                          {formatMoney(payment.amount)}
                        </StyledTableCell>

                        <StyledTableCell>
                          {payment.payment_method || "---"}
                        </StyledTableCell>

                        <StyledTableCell>
                          {payment.reference_no || "---"}
                        </StyledTableCell>

                        <StyledTableCell>
                          {payment.remarks || "---"}
                        </StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <StyledTableCell colSpan={5} align="center">
                        No payment history
                      </StyledTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </DialogContent>

    <DialogActions>
      <Button onClick={onClose}>Close</Button>

      <Button
        variant="contained"
        color="primary"
        onClick={onSave}
        disabled={
          saving || loading || Number(invoice?.balance_amount || 0) <= 0
        }
      >
        {saving ? "Saving..." : "Add Payment"}
      </Button>
    </DialogActions>
  </Dialog>
);

PaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  invoice: PropTypes.object,
  payments: PropTypes.array,
  form: PropTypes.shape({
    amount: PropTypes.string,
    method: PropTypes.string,
    reference: PropTypes.string,
    remarks: PropTypes.string,
    date: PropTypes.string,
  }).isRequired,
  onFormChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

PaymentDialog.defaultProps = {
  loading: false,
  saving: false,
  payments: [],
};

export default PaymentDialog;
